import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ACTIONS, FilterDTO, SELECTORS } from '@oneaudi/stck-store';
import { useI18n } from '@oneaudi/i18n-context';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { SortOption } from '@oneaudi/vtp-configuration-service';

import { Headline, SelectField, Text } from '@oneaudi/unified-web-components';
import { Popover } from '@oneaudi/unified-web-alpha-components';
import { SYS_BREAKPOINT_MD } from '@oneaudi/unified-web-common';

import {
  DangerousElement,
  DynamicComponent,
  dynamicStylingHelper,
  fetchResults,
  generateFilterRequestParam,
  useServicesContext,
  getPLPSession,
  saveSortPLPSession,
} from '@oneaudi/vtp-shared';

import {
  BottomRowContainer,
  CountAndHeadlineContainer,
  CountAndSortContainer,
  SortContainer,
  StyledCount,
  StyledIconButton,
  TopHeadlineContainer,
} from './ResultsBar.styles';

import { useScsUrlParts } from '../../hooks/useScsUrlParts';
import { getAppContentConfiguration } from '../../hooks/tileHook';

import { trackSorting } from '../../tracking/tilesTracking';
import { ShareButton } from './ShareButton';

interface ResultsBarProps {
  count: string;
  label: string;
}

const ResultsBar: React.FC<ResultsBarProps> = ({ count, label }) => {
  const getTranslatedOption = (value: string) => {
    // Some markets wish to maintain all text keys for the sorting options separately [CSR-1048]
    // So we first try to get a direct mapping from `value` of the i18n.
    // if a direct mapping is available we use this otherwise we proceed with the existing logic
    const directTranslatedOption = useI18n({
      id: `nemo.sc.sort.${value?.replace(':', '.')}`,
      defaultMessage: '',
    });
    if (directTranslatedOption) return directTranslatedOption;

    const splittedVal = value.split(':');
    const sortParam = useI18n({ id: `nemo.sc.sort.${splittedVal[0]}` });

    const ascOrDesc =
      splittedVal.length > 1 && splittedVal[1] !== 'single'
        ? useI18n({ id: `nemo.sc.sort.${splittedVal[1]}` })
        : '';
    return `${sortParam} ${ascOrDesc}`;
  };
  const {
    sortParams_defaultOption,
    sortParams_options,
    overWriteSorting_filterToTriggerSorting,
    overWriteSorting_overrideDefaultSortingKey,
    scs_scsMarketPath,
  } = getAppContentConfiguration()?.vtpConfiguration?.fields ?? {};

  // Configured in Content Editor
  // name of the filter that should enforce a resorting
  const filterToTriggerResorting = overWriteSorting_filterToTriggerSorting || '';
  // name of the key in which resorting should happen
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [sortValue, setSortValue] = React.useState(useI18n({ id: 'nemo.sc.sort.relevance' }));
  const scsUrlParts = useScsUrlParts();
  const isUsedCarMkt = scsUrlParts?.marketPath?.includes('uc');
  // This following tooltip text needs to be shown (only) for DE market as a backup if textkey value is not maintained or is " " in AEM
  const sortingInfo =
    'Die Ergebnisse Ihrer Suchanfrage werden nach dem Parameter "Preis ansteigend" gelistet und präsentiert. Sie können über die "Filterfunktion/Sortieren nach"-Funktion eigene Kriterien für Ihre Suchanfrage bestimmen.';
  const sortExplanationNC: string = useI18n({
    id: 'nemo.ui.sc.nc.result.car.sorting.explanation',
    defaultMessage: '',
  });
  const sortExplanationUC: string = useI18n({
    id: 'nemo.ui.sc.uc.result.car.sorting.explanation',
    defaultMessage: '',
  });
  const allVehiclesHeadline = useI18n({
    id: 'nemo.ui.sc.plp.headline.allVehicles',
    defaultMessage: '',
  });
  const sortSelectLabel = useI18n({
    id: 'nemo.ui.sc.plp.sort.label',
    defaultMessage: '',
  });

  const favoritesHeadline = useI18n({
    id: 'stockcars.plp.favorites.headline',
    defaultMessage: '',
  });

  const favoriteVehicleIDs = useSelector((state: unknown) =>
    SELECTORS.VEHICLES.getFavoriteVehicleIdsState(state)
  ) as string[];

  const hasCampaignResults = useSelector((state) => {
    if (!('getFilterGroups' in SELECTORS.FILTERS)) {
      return {};
    }
    const filterGroups = SELECTORS.FILTERS.getFilterGroups(state);
    const campaigns = Object.values(filterGroups?.campaigns?.members || {});
    const campaignsWithinResults = campaigns.filter((campaign: any) => campaign.count > 0);
    return campaignsWithinResults.length > 0;
  });

  const tileContext = getAppContentConfiguration().appContext;

  /* eslint-disable no-nested-ternary */
  const sortExplanationNCLabel =
    sortExplanationNC.trim() !== ''
      ? sortExplanationNC
      : scsUrlParts?.market === 'de'
        ? sortingInfo
        : '';
  const sortExplanationUCLabel =
    sortExplanationUC.trim() !== ''
      ? sortExplanationUC
      : scsUrlParts?.market === 'de'
        ? sortingInfo
        : '';
  /* eslint-enable no-nested-ternary */
  const sortingInfoAriaLabel = useI18n({
    id: 'stockcars.sorting.additionalinfo.arialabel',
    defaultMessage: '',
  });
  const sortParams =
    (sortParams_options?.length ?? 0) > 0
      ? sortParams_options?.filter(
          (param: SortOption) => param !== null && (param as string) !== ''
        )
      : [];
  const dispatch = useDispatch();
  const { filters, presets, isLocationSet } = useSelector((state: any) => {
    const storedSortValue = SELECTORS.SORTING.getSortingStateRaw(state);

    if (storedSortValue?.results !== 'relevance:asc' && storedSortValue?.results !== sortValue) {
      setSortValue(storedSortValue.results);
    }

    return {
      filters: SELECTORS.FILTERS.getSelectedFiltersMap(state) as Map<string, FilterDTO>,
      presets: SELECTORS.FILTERS.getPersistedPresetsMap(state),
      entryUrl: SELECTORS.ENTRY_URL.getEntryUrl(state),
      isLocationSet: SELECTORS.UI.getShowDistance(state),
    };
  });

  const { getEnvironmentConfig } = useServicesContext();
  const fetchFilteredResults = async (requestParam: any) => {
    if (tileContext === 'favorites') {
      fetchResults(
        getEnvironmentConfig()!.scs?.baseUrl,
        scs_scsMarketPath,
        requestParam,
        getEnvironmentConfig()!.scs.apiKey
      ).then((response: any) => {
        const vehicleIds = response?.vehicleBasic?.map((vehicle: { id: any }) => vehicle.id) || [];
        dispatch(ACTIONS.FAVORITE_VEHICLES.setFavoriteVehicleIds({ vehicleIds }));
      });
    } else {
      fetchResults(
        getEnvironmentConfig()!.scs.baseUrl,
        scs_scsMarketPath,
        requestParam,
        getEnvironmentConfig()!.scs.apiKey
      ).then((response: any) => {
        dispatch(ACTIONS.FILTERS.handleFilterResponse({ response }));
      });
    }
  };

  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;

  const setSortingAndFetchResults = (sortingLabel: string, sortingKey: string): void => {
    setSortValue(sortingLabel);
    dispatch(ACTIONS.SORTING.setSorting({ sorting: { results: sortingKey } }));
    const requestParam = generateFilterRequestParam(filters, presets);
    requestParam.set('size', tileContext === 'favorites' ? count : 12);
    requestParam.set('sort', sortingKey);
    if (tileContext === 'favorites') {
      requestParam.set(
        'filter',
        favoriteVehicleIDs?.map((vehId) => 'vehicle.'.concat(vehId))
      );
    }
    fetchFilteredResults(requestParam);
  };

  const inputRef = React.useRef<HTMLSelectElement>(document.createElement('select'));
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();

    const currentOptionIndex = inputRef.current.options.selectedIndex;
    const currentSortLabel = inputRef.current.options[currentOptionIndex]?.label;
    const currentSortParam = inputRef.current.options[currentOptionIndex]?.value;
    setSortingAndFetchResults(currentSortLabel, currentSortParam);
    trackSorting(trackingService, currentSortLabel, currentSortParam);
    saveSortPLPSession(currentSortParam);
  };

  const defaultOption = sortParams_defaultOption;
  const allSortParams = defaultOption && sortParams ? [defaultOption!, ...sortParams] : sortParams;
  const allParamsWithoutDuplicates = [...new Set(allSortParams)];
  const getSortParamsExclDist = (): string[] =>
    allParamsWithoutDuplicates.filter((param: string) => !param.includes('byDistance'));
  let sortParamsToShow = isLocationSet ? allParamsWithoutDuplicates : getSortParamsExclDist();
  sortParamsToShow = hasCampaignResults
    ? sortParamsToShow
    : sortParamsToShow.filter((param) => param !== 'actionVehicles');

  const [isMdViewport, setIsMdViewport] = useState(false);
  // used to check viewport size
  useEffect(() => {
    const checkViewportSize = () => {
      // eslint-disable-next-line radix
      const mdBreakpoint = parseInt(SYS_BREAKPOINT_MD);
      setIsMdViewport(window.innerWidth < mdBreakpoint);
    };
    checkViewportSize();
    window.addEventListener('resize', checkViewportSize);

    return () => window.removeEventListener('resize', checkViewportSize);
  }, []);

  const [currentFilterWhichTriggersResorting, setcurrentFilterWhichTriggersResorting] =
    React.useState<FilterDTO | null>(null);

  const rawSortingState = useSelector((state) => SELECTORS.SORTING.getSortingStateRaw(state));

  useEffect(() => {
    const distanceSorting = 'byDistance:asc';
    const isESmarket = scsUrlParts.market === 'es';
    if (isLocationSet && !isESmarket) {
      dispatch(
        ACTIONS.SORTING.setSorting({
          sorting: { results: distanceSorting, favorites: distanceSorting },
        })
      );
    } else if (!isLocationSet && !filters.get(filterToTriggerResorting)) {
      const sortingValueFromSession = getPLPSession();
      dispatch(
        ACTIONS.SORTING.setSorting({
          sorting: {
            results: sortingValueFromSession?.sortingInSession
              ? sortingValueFromSession?.sortingInSession || rawSortingState.results
              : sortParamsToShow[0],
            favorites: sortingValueFromSession?.sortingInSession
              ? sortingValueFromSession?.sortingInSession || rawSortingState.favorites
              : sortParamsToShow[0],
          },
        })
      );
    } else if (
      filters.get(filterToTriggerResorting) &&
      sortValue !== overWriteSorting_overrideDefaultSortingKey
    ) {
      const filterWhichTriggersResorting = filters.get(filterToTriggerResorting)!;
      if (
        currentFilterWhichTriggersResorting &&
        filterWhichTriggersResorting.values[0] === currentFilterWhichTriggersResorting?.values[0] &&
        filterWhichTriggersResorting.values[1] === currentFilterWhichTriggersResorting.values[1]
      ) {
        return;
      }
      dispatch(
        ACTIONS.SORTING.setSorting({
          sorting: { results: overWriteSorting_overrideDefaultSortingKey },
        })
      );
      setcurrentFilterWhichTriggersResorting(filters.get(filterToTriggerResorting)!);
    }
  }, []);

  /* This regex retrieves all numeric characters from a formatted count string. */
  const modifiedCount = count ? count?.replace(/\D/g, '') : '';

  const getPopoverBody = (sortExplanation: any): JSX.Element => (
    <>
      <Popover
        mobileMaxContent
        content={
          <PopoverContent aria-label="sorting info">
            <Text tag="div" variant={{ order: '1', style: 'normal', type: 'copy' }}>
              <DangerousElement sanitize html={sortExplanation} />
            </Text>
          </PopoverContent>
        }
        id="sorting-explanation-popover"
        onClose={() => setIsPopoverOpen(false)}
        triggerElement={
          <StyledIconButton
            variant="tertiary"
            data-testid="sortingTooltip"
            size="sm"
            onClick={() => setIsPopoverOpen((prev) => !prev)}
            icon="info-circle"
            aria-label={sortingInfoAriaLabel}
            disabled={!(Number(modifiedCount) > 1)}
          />
        }
        withTip
        open={isPopoverOpen}
      />
    </>
  );

  const headlineTxt = tileContext === 'results' ? allVehiclesHeadline : favoritesHeadline;

  return (
    <CountAndSortContainer data-testid="countSortContainer">
      <TopHeadlineContainer>
        <DynamicComponent
          legacy={
            <CountAndHeadlineContainer>
              <Headline
                tag="h4"
                variant={{ order: '4', type: 'headline', weight: 'normal' }}
                data-testid="allVehiclesHeadline"
              >
                {`${headlineTxt}`}
              </Headline>
              <StyledCount data-testid="vehicleCount">
                <Text
                  variant={{ order: '1', style: 'normal', type: 'copy' }}
                >{`(${count}${!isMdViewport && tileContext === 'results' ? ` ${label}` : ''})`}</Text>
              </StyledCount>
              {tileContext === 'results' && <ShareButton />}
            </CountAndHeadlineContainer>
          }
        />
        <DynamicComponent
          alpha={
            <CountAndHeadlineContainer>
              {tileContext === 'results' && (
                <Headline
                  tag="h2"
                  variant={{ order: '4', type: 'headline', weight: 'normal' }}
                  data-testid="allVehiclesHeadline"
                >
                  {`${headlineTxt}`}
                </Headline>
              )}
              {tileContext === 'favorites' && (
                <Headline
                  tag="h4"
                  variant={{ order: '4', type: 'headline', weight: 'normal' }}
                  data-testid="allVehiclesHeadline"
                >
                  {`${count} ${headlineTxt}`}
                </Headline>
              )}
              <StyledCount data-testid="vehicleCount">
                {tileContext === 'results' && (
                  <Text
                    variant={{ order: '1', style: 'normal', type: 'copy' }}
                  >{`(${count}${!isMdViewport && tileContext === 'results' ? ` ${label}` : ''})`}</Text>
                )}
              </StyledCount>
              {tileContext === 'results' && <ShareButton />}
            </CountAndHeadlineContainer>
          }
        />
      </TopHeadlineContainer>
      <BottomRowContainer>
        {sortParamsToShow && sortParamsToShow.length ? (
          <SortContainer>
            <SelectFieldWrapper>
              <SelectField
                id="sorting"
                label={dynamicStylingHelper('', sortSelectLabel)}
                value={sortValue}
                required
                ref={inputRef}
                onChange={handleChange}
                data-testid="sort-select"
                disabled={!(Number(modifiedCount) > 1)}
              >
                {sortParamsToShow.map((sortParam: string) => (
                  <option key={sortParam} value={sortParam}>
                    {getTranslatedOption(sortParam)}
                  </option>
                ))}
              </SelectField>
            </SelectFieldWrapper>
            {!isUsedCarMkt && sortExplanationNCLabel && getPopoverBody(sortExplanationNCLabel)}
            {isUsedCarMkt && sortExplanationUCLabel && getPopoverBody(sortExplanationUCLabel)}
          </SortContainer>
        ) : (
          <></>
        )}
      </BottomRowContainer>
    </CountAndSortContainer>
  );
};

export default ResultsBar;

export const SelectFieldWrapper = styled.div`
  width: ${dynamicStylingHelper('unset', 'calc(100% - 49px)')};

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    width: unset;
  }
`;

export const PopoverContent = styled.div`
  max-width: ${dynamicStylingHelper('unset', '740px')};
`;
